/**
 * @fileoverview The type door of Today's start card, "Type a plant": the add-a-plant
 * form, opened over Today (B30, design revision 5).
 *
 * It is the very form the Plants page opens (PlantFormDialog) and saves the
 * way Plants does, through PlantsContext's addPlant, so the plant, its first
 * stage and the refreshed list are one code path. Saving keeps the gardener on
 * Today, which then shows the plant; cancelling closes the form and leaves the
 * doors. Nothing navigates.
 *
 * TodayPage owns this hook and draws its element once, outside the views, so
 * the form is not unmounted when the doors give way to Today on the first
 * plant (design rev 3), exactly as the photo door's sheet.
 *
 * Where: TodayPage, for WelcomePlace's type door.
 * API: POST /plants through usePlantsContext().addPlant (as the Plants page).
 * Words (en/es): plants.form.saveFailed (a refusal the form says).
 * Also gives useSaveNewPlant, the save the type door's form takes.
 *
 * @module useDoorAddForm
 */

import React from 'react';
import { PlantFormDialog } from '@/features/plant-list/components/dialogs/PlantFormDialog';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';

/** What the hook hands back. */
export interface DoorAddFormControl {
  /** Opens the add form. */
  open: () => void;
  /** The form's dialog: render it once, anywhere. */
  element: React.ReactNode;
}

/**
 * How a new plant is saved from a form opened over another page: through PlantsContext's addPlant, as the Plants page
 * does; a refusal is thrown for the form to say.
 *
 * @returns The save the plant form takes.
 */
export function useSaveNewPlant(): (payload: PlantWritePayload) => Promise<number | null> {
  const { reactTranslate: t } = useTranslation();
  const { addPlant } = usePlantsContext();
  return async (payload) => {
    const result = await addPlant(payload);
    if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
    return result.plant_obj_id != null ? Number(result.plant_obj_id) : null;
  };
}

/**
 * The add form for the type door, and its save.
 *
 * @returns open(), and the element to render once.
 */
export function useDoorAddForm(): DoorAddFormControl {
  /** True while the form is open. */
  const [open, setOpen] = React.useState(false);
  const save = useSaveNewPlant();

  const element = <PlantFormDialog plant={null} isOpen={open} onClose={() => setOpen(false)} onSave={save} />;
  return { open: () => setOpen(true), element };
}
